import pino from "pino";
import { FrontLoggerSymbol, type IFrontLoggerShipping } from "@whatslly/commons/frontLogger/types";
import { inject, injectable } from "inversify";
import { serializeError } from "serialize-error";

const logLevel = import.meta.env.VITE_LOG_LEVEL ?? "error";
const pinoConfig: pino.LoggerOptions = {
  level: logLevel,
  enabled: !import.meta.env.PROD,
  browser: {
    asObject: true,
    serialize: true,
  },
};

if (import.meta.env.VITE_ENABLED_PINO) {
  pinoConfig.enabled = import.meta.env.VITE_ENABLED_PINO === "true";
}

const pinoLogger = pino(pinoConfig);
const getLogger = (module: string): pino.Logger => pinoLogger.child({ module });

export type IFrontLogger = {
  getLogger(name: string): IFrontLogger;
  info(obj: Record<string, unknown> | string, msg?: string): void;
  debug(obj: Record<string, unknown> | string, msg?: string): void;
  warn(obj: Record<string, unknown> | string, msg?: string): void;
  error(obj: Record<string, unknown> | string, msg?: string): void;
};
@injectable()
export class FrontLogger implements IFrontLogger {
  logger: pino.Logger | undefined;
  name?: string;

  constructor(@inject(FrontLoggerSymbol) private frontShipLogger: IFrontLoggerShipping) {}

  getLogger(name: string) {
    const newFrontLogger = new FrontLogger(this.frontShipLogger);
    newFrontLogger.name = name;
    newFrontLogger.logger = getLogger(name);
    return newFrontLogger;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info(obj: Record<string, any> | string, msg?: string) {
    this.shipLog(obj, msg, "info");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug(obj: Record<string, any> | string, msg?: string) {
    this.shipLog(obj, msg, "debug");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn(obj: Record<string, any> | string, msg?: string) {
    this.shipLog(obj, msg, "warn");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(obj: Record<string, any> | string, msg?: string) {
    this.shipLog(obj, msg, "error");
  }

  private shipLog(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    obj: Record<string, any> | string,
    msg: string | undefined,
    level: "info" | "error" | "warn" | "debug"
  ) {
    if (!this.logger || !this.name) {
      return;
    }
    if (typeof obj === "string" && typeof msg === "undefined") {
      this.logger[level](obj);
      this.frontShipLogger.shipLog({ msg: obj }, level, this.name);
    }

    if (typeof obj === "object" && typeof msg === "string") {
      this.logger[level](obj, msg);

      if (obj.err) {
        obj.err = serializeError(obj.err);
      }
      this.frontShipLogger.shipLog({ obj, msg }, level, this.name);
    }
  }
}
